import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { uppercaseFirstLetter } from '../../../../utils/functions';
import { useHandleInvitations } from '../hooks/useHandleInvitations';

import { Button } from '@/components';
import {
  Sidebar,
  TourneyList,
  TourneySearch,
} from '@/components/GenericsTournaments';
import { InscriptionSearchingResult } from '@/components/GenericsTournaments/SearchingResult';
import { ItemAutocomplete } from '@/components/InputAutocomplete';
import { DefaultTabsProps } from '@/components/Tabs';
import { TabType } from '@/components/Types';
import useAuth from '@/hooks/useAuth';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import {
  requestAutocompleteSuggestions,
  requestSearchMinileagues,
} from '@/services/minileagues';
import { MiniLigasTO, RequestMiniliguesSearch } from '@/types/minileagues';
import { GenericItemTourneysData } from '@/types/tda';
import {
  cn,
  concatenateArraysWithIntervals,
  findDifferentsStringsArrays,
  GENERICS_WORDS,
  participantsMl,
  TEXTS,
} from '@/utils';

interface ParamsRequest {
  valueSearch: string;
  page: number;
}

const ITEM_PER_PAGE_MOBILE = 11;
const ITEM_PER_PAGE_DESKTOP = 6;

export const SideBarContent = React.memo(({ list }: { list: MiniLigasTO }) => {
  const { isDesktop, isMobile } = useIsDesktop();
  const navigate = useNavigate();
  const { tourneyData, updateTourney } = useTourney();
  const [sendForm, setSendForm] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [termSearching, setTermSearching] = useState<string | null>(null);
  const [searchResult, setSearchResult] = useState<
    GenericItemTourneysData[] | null
  >(null);
  const [recommended, setRecommended] = useState<
    GenericItemTourneysData[] | null
  >(null);
  const [listSuggested, setListSuggested] = useState<ItemAutocomplete[]>([]);
  const [myMinileaguesNames, setMyMinileaguesNames] = useState<string[]>([]);
  const { tab, handleTabChanges } = useHandleChangesTabs();
  const [cantidadBusquedas, setCantidadBusquedas] = useState(0);

  const toast = useToast();
  const { userInitialData } = useAuth();
  const handleButtonCreate = () => {
    if (userInitialData?.dtExtranjero) {
      return toast.show(TEXTS.exclusiveForArgentinianDTs);
    }
    navigate(ROUTES.minileagues.create);
  };

  // REQUEST BUSQUEDA MINILIGAS
  const {
    mutateAsync: getMinileagues,
    isLoading,
    data: dataMinileagues,
    status,
  } = useMutation(
    async (data: RequestMiniliguesSearch) =>
      await requestSearchMinileagues(data)
  );

  const { onHandleSubmit } = useHandleInvitations(recommended);

  const { onHandleSubmit: onHandleSubmitSearching } = useHandleInvitations(
    searchResult,
    tab
  );

  // tabs buscador
  const tabs: DefaultTabsProps['tabs'] = useMemo(() => {
    return [
      { name: GENERICS_WORDS.minileagues },
      { name: GENERICS_WORDS.coachs },
    ];
  }, []);

  const participants = useCallback((amount: number): string => {
    const rest = amount < tourneyData.maxNumParticipants - 1;
    const participants =
      (rest ? 'Faltan ' : 'Falta ') +
      (tourneyData.maxNumParticipants - amount || 0) +
      ' ' +
      (rest ? GENERICS_WORDS.participants : GENERICS_WORDS.participant);

    return participants;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const minileaguesPending = useMemo(() => {
    return tourneyData.tourneysPending as MiniLigasTO[];
  }, [tourneyData.tourneysPending]);

  const minileaguesPlaying = useMemo(() => {
    return tourneyData.tourneysPlaying as MiniLigasTO[];
  }, [tourneyData.tourneysPlaying]);

  // creo array de string con miniligas de las que soy dueño para comparar y no mostrarlos en resultados de autocomplete
  useEffect(() => {
    const stringArr: string[] = [];
    minileaguesPlaying &&
      // eslint-disable-next-line array-callback-return
      minileaguesPlaying.map((miniliga) => {
        if (miniliga.idUsuarioOwner === userInitialData?.idUsuarioDT) {
          return stringArr.push(miniliga.nombreML || '');
        }
      });
    setMyMinileaguesNames(stringArr);
  }, [minileaguesPlaying, userInitialData?.idUsuarioDT]);

  // LISTA DE SUGERIDOS
  const initState = useCallback(() => {
    if (list && list.length > 0) {
      const formatedList = list.map((item: MiniLigasTO) => ({
        id: item.id,
        title: item.nombreML,
        subtitle: `${item?.nombreAdm} ${item?.apellidoAdm}`,
        disabled:
          Object.values(minileaguesPending.filter((obj) => obj.id === item.id))
            .length !== 0 || tourneyData.idsPendings.includes(item.id || 0),
        secundaryText: participantsMl(
          item?.cantidadParticipantes || 0,
          tourneyData.maxNumParticipants
        ),
      }));

      setRecommended(() => {
        return formatedList;
      });
    } else {
      setRecommended(() => {
        return null;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, participants]);

  // TODO: ordenar con mayor cantidad de participantes
  const sortMiniligues = (minileagues: MiniLigasTO[]) => {
    return minileagues.sort((a: MiniLigasTO, b: MiniLigasTO) => {
      return (a.nombreML || '').localeCompare(b.nombreML || '');
    });
  };

  const itemSearchIsPlaying = useCallback(
    (id: number): MiniLigasTO | undefined => {
      if (minileaguesPlaying && minileaguesPlaying.length > 0) {
        return (
          minileaguesPlaying && minileaguesPlaying.find((obj) => obj.id === id)
        );
      }
    },
    [minileaguesPlaying]
  );

  // CREO LISTA GENERICA DE BUSQUEDA DE GRUPO
  const createListItemsMiniligues = useCallback(
    (listResponse: MiniLigasTO[]) => {
      const filtered = listResponse.filter((item) => item.idEstadoML !== 2);
      const sortedMiniligues = sortMiniligues(filtered);
      const list = sortedMiniligues.reduce<GenericItemTourneysData[]>(
        (acc, item) => {
          const txtPlaying = item.id
            ? itemSearchIsPlaying(item.id)
              ? 'Participando'
              : ''
            : '';

          acc.push({
            id: item.id,
            action: {
              txtAction: 'Enviar solicitud',
              txtActionDisabled: txtPlaying || 'Esperando aceptación',
              variantButton: 'secondary',
            },
            title: item.nombreML,
            subtitle: `${item.nombreAdm} ${item.apellidoAdm}`,
            disabled:
              minileaguesPending.filter((obj) => obj.id === item.id).length !==
                0 ||
              tourneyData.idsPendings.includes(item.id || 0) ||
              Boolean(txtPlaying),
            secundaryText: participantsMl(
              item?.cantidadParticipantes || 0,
              tourneyData.maxNumParticipants
            ),
          });

          return acc;
        },
        []
      );

      return list;
    },
    [
      itemSearchIsPlaying,
      minileaguesPending,
      tourneyData.idsPendings,
      tourneyData.maxNumParticipants,
    ]
  );

  // seteo lista de items genericos por grupo
  const setGenericListMinileagues = useCallback(
    (list: GenericItemTourneysData[]) => {
      setSearchResult([]);
      handleTabChanges(TabType.FirstTab);
      setSearchResult(list);
    },
    [handleTabChanges]
  );

  // seteo lista de items genericos segun servicio de busqueda
  const setGenericListDts = useCallback(
    (list: GenericItemTourneysData[]) => {
      setSearchResult([]);
      handleTabChanges(TabType.SecondTab);
      setSearchResult(list);
    },
    [handleTabChanges]
  );

  const sortDts = (dtsList: MiniLigasTO[]) => {
    // Count frequency of idUsuarioOwner
    const ownerFrequency = dtsList.reduce((acc: any, dts: MiniLigasTO) => {
      if (dts.id !== undefined) {
        acc[dts.id] = (acc[dts.id] || 0) + 1;
      }
      return acc;
    }, {});

    return dtsList.sort((a, b) => {
      const freqDiff =
        (b.id && ownerFrequency[b.id]) - (a.id && ownerFrequency[a.id]);
      if (freqDiff !== 0) return freqDiff;

      return (a.nombreML || '').localeCompare(b.nombreML || '');
    });
  };

  const createListItemsDts = useCallback(
    (listResponse: MiniLigasTO[]) => {
      // Sort the dts
      const sortedDts = sortDts(listResponse);
      const list = sortedDts.reduce<GenericItemTourneysData[]>((acc, item) => {
        const txtPlaying = itemSearchIsPlaying(item.id || 0)
          ? 'Participando'
          : '';

        acc.push({
          id: item.id,
          action: {
            txtAction: 'Enviar solicitud',
            txtActionDisabled: txtPlaying || 'Esperando aceptación',
            variantButton: 'secondary',
          },
          disabled:
            Object.values(
              minileaguesPending.filter((obj) => obj.id === item.id)
            ).length !== 0 ||
            tourneyData.idsPendings.includes(item.id ?? 0) ||
            Boolean(txtPlaying),
          title: `${item.nombreAdm} ${item.apellidoAdm}`,
          profilePicUrl: item.fotoPerfil,
          profileName:
            uppercaseFirstLetter(String(item?.nombreAdm)) +
            ' ' +
            uppercaseFirstLetter(String(item?.apellidoAdm)),
          subtitle: isMobile
            ? item.nombreML?.toUpperCase()
            : item.nombreML ?? '',
          secundaryText: item.provinciaAdm,
        });

        return acc;
      }, []);
      return list;
    },
    [isMobile, itemSearchIsPlaying, tourneyData.idsPendings, minileaguesPending]
  );

  const handleSetGenericsByTabs = useCallback(
    (tab: TabType, data: MiniLigasTO[]) => {
      if (tab === TabType.FirstTab) {
        setGenericListMinileagues(createListItemsMiniligues(data));
      } else {
        setGenericListDts(createListItemsDts(data));
      }
    },
    [
      setGenericListMinileagues,
      createListItemsMiniligues,
      setGenericListDts,
      createListItemsDts,
    ]
  );

  const handleFilterByTab = useCallback(
    async (tab: TabType) => {
      tab && handleTabChanges(tab);
      setSendForm(true);
      if (dataMinileagues && status && dataMinileagues.page) {
        handleSetGenericsByTabs(tab, dataMinileagues.page?.objectList || []);
      }
    },
    [handleTabChanges, dataMinileagues, status, handleSetGenericsByTabs]
  );

  // manejo de envio de consulta para miniligas
  const onHandleRequest = useCallback(
    async ({ valueSearch, page }: ParamsRequest) => {
      setListSuggested([]);
      try {
        setTermSearching(valueSearch);
        const response = await getMinileagues({
          paginaActual: page,
          cantPorPagina: isMobile
            ? ITEM_PER_PAGE_MOBILE
            : ITEM_PER_PAGE_DESKTOP,
          nombreDueno: '',
          nombreMiniLiga: tab === TabType.FirstTab ? valueSearch : '',
          apellidoDueno: tab === TabType.SecondTab ? valueSearch : '',
        });

        if (response.estado && response?.page) {
          if (response?.page?.objectList) {
            setTableMinileaguesResult(page, response.page.objectList);
          }
          setHasMore(Boolean(response.page?.hasNext));
          setCurrentPage(page);
          setCantidadBusquedas((prev) => prev + 1);
        } else {
          toast.show(TEXTS.defaultError);
        }
      } catch (error) {
        console.error(TEXTS.defaultError);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getMinileagues, tab, toast, isMobile]
  );

  const loadMore = useCallback(
    async (pageNumber?: number) => {
      if (isLoading || !dataMinileagues) return;
      const nextPage = pageNumber || currentPage + 1;
      termSearching &&
        (await onHandleRequest({
          valueSearch: termSearching,
          page: nextPage,
        }));
    },
    [isLoading, dataMinileagues, currentPage, onHandleRequest, termSearching]
  );

  const setTableMinileaguesResult = useCallback(
    (page: number, list: MiniLigasTO[]) => {
      if (tab === TabType.FirstTab) {
        const listItemsMinileagues = createListItemsMiniligues(list);
        if (page === 1) {
          setGenericListMinileagues(listItemsMinileagues);
        } else {
          const newPage = listItemsMinileagues;
          searchResult &&
            setGenericListMinileagues([...searchResult, ...newPage]);
        }
      } else {
        const listItemsDTs = createListItemsDts(list);
        if (page === 1) {
          setGenericListDts(listItemsDTs);
        } else {
          const newPage = listItemsDTs;
          searchResult && setGenericListDts([...searchResult, ...newPage]);
        }
      }
    },
    [
      createListItemsDts,
      createListItemsMiniligues,
      searchResult,
      setGenericListDts,
      setGenericListMinileagues,
      tab,
    ]
  );

  // request de sugerencias de autocomplete (string)
  const requestAutocomplete = useCallback(async (term: string) => {
    const searchTerm = term.toLowerCase();

    // Utilizamos el mismo servicio de búsqueda pero con un tamaño de página pequeño
    // para obtener sugerencias rápidas
    const response = await requestAutocompleteSuggestions(
      searchTerm,
      searchTerm
    );

    return response;
  }, []);

  // set list autocomplete
  const listSuggestionsAutocomplete = useCallback(
    async (term: string) => {
      setSendForm(false);

      if (term.length < 3) {
        setSearchResult(null);
        return;
      }

      const response = await requestAutocomplete(term.trim());

      const dts = response.responseTourneyDts;
      const tdas = response.responseTourneyName;

      if (dts.estado && tdas.estado) {
        const suggestions = concatenateArraysWithIntervals(
          dts.sugerencias,
          tdas.sugerencias,
          3
        );

        // creo array  de string para comparar diferencias
        const newArrSuggestions: string[] = [];
        suggestions.map((item) => {
          return newArrSuggestions.push(item.texto);
        });

        // filtro el resultado de mis torneos y otros torneos.. retorno listado sin mis torneos
        const suggestionsWithoutMe = findDifferentsStringsArrays(
          myMinileaguesNames,
          newArrSuggestions
        );

        const mapSuggestions = suggestionsWithoutMe.reduce<ItemAutocomplete[]>(
          (option, name, index) => {
            option.push({
              id: index,
              value: name,
            });
            return option;
          },
          []
        );

        setListSuggested(mapSuggestions);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [myMinileaguesNames]
  );

  // INIT STATE = SUGERIDOS
  useEffect(() => {
    initState();

    return () => {
      setRecommended(() => {
        return null;
      });
    };
  }, [initState]);

  const onHandleRefreshAfterInvitation = useCallback(
    (ids: number[], isPendings = true) => {
      const numberSet = new Set(ids);

      const disabledParticipant = {
        action: {
          txtAction: 'Enviar solicitud',
          txtActionDisabled: 'Participando',
        },
      };

      // OJO POSIBLE MENTE VOLVER A COMENTAR
      setSearchResult((prevSearchResult) => {
        if (!prevSearchResult) return null;
        return prevSearchResult.map((item) => ({
          ...item,
          disabled: (item.id && numberSet.has(item.id)) || item.disabled,
          ...(!isPendings && disabledParticipant),
        }));
      });

      setRecommended((prevRecommend) => {
        if (!prevRecommend) return null;
        return prevRecommend.map((item) => ({
          ...item,
          disabled: (item.id && numberSet.has(item.id)) || item.disabled,
          ...(!isPendings && disabledParticipant),
        }));
      });
    },
    []
  );

  useEffect(() => {
    if (tourneyData.idsPendings.length > 0) {
      onHandleRefreshAfterInvitation(tourneyData.idsPendings, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.idsPendings]);

  useEffect(() => {
    if (tourneyData.idsPlayings.length > 0) {
      onHandleRefreshAfterInvitation(tourneyData.idsPlayings, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.idsPlayings]);

  return (
    <Sidebar>
      {isDesktop && !tourneyData.tourneyStarted && (
        <Button
          className="w-full"
          disabled={tourneyData.tourneyStarted}
          type="button"
          variant="primary"
          onClick={handleButtonCreate}
        >
          Crear Miniliga
        </Button>
      )}

      {/* Buscador grupos existentes */}
      <TourneySearch
        autocomplete={true}
        className="mt-6 lg:mt-11"
        label="Inscribite a una Miniliga existente"
        listSuggested={listSuggested}
        listSuggestionsAutocomplete={listSuggestionsAutocomplete}
        placeholder="Nombre de la Miniliga o administrador"
        sendForm={sendForm}
        tabSelected={tab}
        onHandleCleanResult={() => {
          setSearchResult(null);
          setCantidadBusquedas(0);
        }}
        onHandleRequest={onHandleRequest}
        onHandleTabSelected={handleTabChanges}
      />

      {searchResult && (
        <div className={cn({ 'min-h-48': searchResult })}>
          <InscriptionSearchingResult
            cantidadBusquedas={cantidadBusquedas}
            hasMore={hasMore}
            list={searchResult}
            loadMore={loadMore}
            tabSelected={tab}
            tabs={tabs}
            withAvatar={tab === TabType.SecondTab}
            onHandleSubmit={onHandleSubmitSearching}
            onSubmitTabSelected={handleFilterByTab}
          />
        </div>
      )}

      {/* Torneos Recomendados */}
      {recommended && (
        <TourneyList
          action={{
            txtAction: 'Enviar solicitud',
            txtActionDisabled: 'Esperando aceptación',
            variantButton: 'secondary',
          }}
          className="mt-4"
          handleSubmit={onHandleSubmit}
          list={recommended || []}
          title="Recomendadas"
        />
      )}
      {isMobile && (
        <Button
          className="mt-8 mx-auto flex justify-center items-center font-bold"
          type="button"
          variant="text"
          onClick={() => {
            updateTourney({ showModalInfo: true });
          }}
        >
          Más información y Premios
        </Button>
      )}
    </Sidebar>
  );
});

SideBarContent.displayName = 'SideBarContent';
